/**
 * AdminRequestPage - Public page for users to request admin access
 * Requires basic registration first, then super admin approval
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { adminRequestAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Calendar, User, Mail, Phone, Building2, FileText, Users,
  Loader2, ArrowRight, ArrowLeft, Check, Shield, Sparkles, CheckCircle2
} from 'lucide-react';

const AdminRequestPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [requestId, setRequestId] = useState(null);
  
  const [form, setForm] = useState({
    // Personal Info
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    // Event Info
    event_name: '',
    event_description: '',
    expected_attendees: '',
    event_date: '',
    // Request Reason
    reason: '',
  });

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const isStep1Valid = form.first_name && form.last_name && form.email;
  const isStep2Valid = form.event_name && form.expected_attendees;
  const isStep3Valid = form.reason && form.reason.length >= 50;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await adminRequestAPI.submit({
        ...form,
        expected_attendees: parseInt(form.expected_attendees) || 0,
      });
      setRequestId(response.data.request_id);
      setIsSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success screen
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <CardTitle className="text-2xl text-white">Request Submitted!</CardTitle>
            <CardDescription className="text-slate-400">
              Your request has been sent to our admin team for review
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
              <p className="text-sm text-slate-400">Request ID</p>
              <code className="text-emerald-400 font-mono text-sm">{requestId}</code>
            </div>
            
            <Alert className="bg-blue-500/10 border-blue-500/30">
              <AlertDescription className="text-blue-300 text-sm">
                What happens next?
                <ul className="list-disc ml-4 mt-2 space-y-1">
                  <li>Our team will review your request within 24-48 hours</li>
                  <li>You will receive an email with approval status</li>
                  <li>Once approved, you will get login credentials to manage your event</li>
                </ul>
              </AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => navigate('/')}
            >
              Return to Home
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Have questions? Contact support@eventflow.com
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-slate-300 transition">
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>
          <Badge variant="outline" className="border-indigo-500/50 text-indigo-300">
            <Shield className="h-3 w-3 mr-1" />
            Admin Registration
          </Badge>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/20 text-indigo-300 mb-4">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">Become an Event Administrator</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Request Admin Access</h1>
          <p className="text-slate-400 max-w-lg mx-auto">
            Create and manage professional events with EventFlow powerful admin tools
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {['Personal Info', 'Event Details', 'Review'].map((label, i) => (
              <div key={i} className="flex items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${step > i + 1 ? 'bg-emerald-500 text-white' : step === i + 1 ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-400'}
                `}>
                  {step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <span className={`ml-2 text-sm hidden sm:inline ${step === i + 1 ? 'text-white' : 'text-slate-500'}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
          <Progress value={(step / 3) * 100} className="h-2 bg-slate-700" />
        </div>

        {/* Step 1: Personal Info */}
        {step === 1 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="h-5 w-5 text-indigo-400" />
                Personal Information
              </CardTitle>
              <CardDescription className="text-slate-400">
                Tell us about yourself so we can set up your admin account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">First Name *</Label>
                  <Input
                    value={form.first_name}
                    onChange={(e) => updateForm('first_name', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    placeholder="John"
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Last Name *</Label>
                  <Input
                    value={form.last_name}
                    onChange={(e) => updateForm('last_name', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    placeholder="Doe"
                    data-testid="input-last-name"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Email Address *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateForm('email', e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  placeholder="john@company.com"
                  data-testid="input-email"
                />
                <p className="text-xs text-slate-500 mt-1">
                  You will receive approval notifications at this email
                </p>
              </div>
              
              <div>
                <Label className="text-slate-300">Phone Number</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => updateForm('phone', e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  placeholder="+1 (555) 123-4567"
                  data-testid="input-phone"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Company / Organization</Label>
                <Input
                  value={form.company}
                  onChange={(e) => updateForm('company', e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  placeholder="Acme Corp"
                  data-testid="input-company"
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button
                className="w-full"
                disabled={!isStep1Valid}
                onClick={() => setStep(2)}
                data-testid="continue-step1"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 2: Event Details */}
        {step === 2 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-indigo-400" />
                Event Details
              </CardTitle>
              <CardDescription className="text-slate-400">
                Tell us about the event you want to create and manage
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-300">Event Name *</Label>
                <Input
                  value={form.event_name}
                  onChange={(e) => updateForm('event_name', e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  placeholder="Tech Conference 2026"
                  data-testid="input-event-name"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Event Description</Label>
                <Textarea
                  value={form.event_description}
                  onChange={(e) => updateForm('event_description', e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white min-h-[100px]"
                  placeholder="A brief description of your event..."
                  data-testid="input-event-description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Expected Attendees *</Label>
                  <Select
                    value={form.expected_attendees}
                    onValueChange={(value) => updateForm('expected_attendees', value)}
                  >
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="select-attendees">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">Up to 50</SelectItem>
                      <SelectItem value="100">50 - 100</SelectItem>
                      <SelectItem value="250">100 - 250</SelectItem>
                      <SelectItem value="500">250 - 500</SelectItem>
                      <SelectItem value="1000">500 - 1000</SelectItem>
                      <SelectItem value="5000">1000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-300">Event Date</Label>
                  <Input
                    type="date"
                    value={form.event_date}
                    onChange={(e) => updateForm('event_date', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    data-testid="input-event-date"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Why do you want to use EventFlow? *</Label>
                <Textarea
                  value={form.reason}
                  onChange={(e) => updateForm('reason', e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white min-h-[120px]"
                  placeholder="Tell us about your event management needs and why EventFlow is right for you... (minimum 50 characters)"
                  data-testid="input-reason"
                />
                <p className={`text-xs mt-1 ${form.reason.length >= 50 ? 'text-emerald-400' : 'text-slate-500'}`}>
                  {form.reason.length}/50 characters minimum
                </p>
              </div>
            </CardContent>
            <CardFooter className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                className="flex-1"
                disabled={!isStep2Valid || !isStep3Valid}
                onClick={() => setStep(3)}
                data-testid="continue-step2"
              >
                Review <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="h-5 w-5 text-indigo-400" />
                Review Your Request
              </CardTitle>
              <CardDescription className="text-slate-400">
                Please review your information before submitting
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Personal Info Summary */}
              <div className="bg-slate-700/30 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" /> Personal Information
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-slate-400">Name</div>
                  <div className="text-white">{form.first_name} {form.last_name}</div>
                  <div className="text-slate-400">Email</div>
                  <div className="text-white">{form.email}</div>
                  {form.phone && (
                    <>
                      <div className="text-slate-400">Phone</div>
                      <div className="text-white">{form.phone}</div>
                    </>
                  )}
                  {form.company && (
                    <>
                      <div className="text-slate-400">Company</div>
                      <div className="text-white">{form.company}</div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Event Info Summary */}
              <div className="bg-slate-700/30 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" /> Event Details
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-slate-400">Event Name</div>
                  <div className="text-white">{form.event_name}</div>
                  <div className="text-slate-400">Expected Attendees</div>
                  <div className="text-white">{form.expected_attendees}+</div>
                  {form.event_date && (
                    <>
                      <div className="text-slate-400">Event Date</div>
                      <div className="text-white">{new Date(form.event_date).toLocaleDateString()}</div>
                    </>
                  )}
                </div>
                {form.event_description && (
                  <div className="mt-3 pt-3 border-t border-slate-600">
                    <div className="text-slate-400 text-sm mb-1">Description</div>
                    <div className="text-white text-sm">{form.event_description}</div>
                  </div>
                )}
              </div>
              
              {/* Reason */}
              <div className="bg-slate-700/30 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-400 mb-2">Request Reason</h4>
                <p className="text-white text-sm">{form.reason}</p>
              </div>
              
              <Alert className="bg-amber-500/10 border-amber-500/30">
                <AlertDescription className="text-amber-300 text-sm">
                  By submitting this request, you agree to EventFlow Terms of Service and acknowledge that your request will be reviewed by our admin team.
                </AlertDescription>
              </Alert>
            </CardContent>
            <CardFooter className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                <ArrowLeft className="mr-2 h-4 w-4" /> Edit
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={handleSubmit}
                disabled={isSubmitting}
                data-testid="submit-request"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Submit Request
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Already have an account */}
        <div className="text-center mt-6">
          <p className="text-slate-500 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-indigo-400 hover:underline">Sign in here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminRequestPage;
